import { useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { Shield, KeyRound, CheckCircle2 } from "lucide-react";
import { api, formatErr } from "@/lib/api";

export default function ResetPassword() {
  const [params] = useSearchParams();
  const token = params.get("token") || "";
  const navigate = useNavigate();
  const [pw, setPw] = useState("");
  const [pw2, setPw2] = useState("");
  const [done, setDone] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  const submit = async (e) => {
    e.preventDefault(); setError("");
    if (pw !== pw2) { setError("Passwords don't match."); return; }
    setLoading(true);
    try { await api.post("/auth/reset-password", { token, new_password: pw }); setDone(true); setTimeout(() => navigate("/login"), 2500); }
    catch (e) { setError(formatErr(e.response?.data?.detail) || e.message); }
    finally { setLoading(false); }
  };

  return (
    <div className="min-h-screen bg-cream flex items-center justify-center px-6 py-12">
      <div className="w-full max-w-md" data-testid="reset-password-page">
        <Link to="/" className="inline-flex items-center gap-2 text-ink"><Shield className="w-5 h-5" strokeWidth={1.5} /><span className="font-serif text-xl">Withdraw Guard</span></Link>
        <h1 className="font-serif text-4xl sm:text-5xl tracking-tight mt-10">Choose a new password.</h1>

        {!token ? (
          <div className="mt-10 text-sm text-clay bg-clay-light px-4 py-3 rounded-lg" data-testid="reset-password-missing">
            This link is missing its token. <Link to="/forgot-password" className="underline">Request a new one</Link>.
          </div>
        ) : done ? (
          <div className="mt-10 bg-linen border border-sand rounded-2xl p-6" data-testid="reset-password-done">
            <CheckCircle2 className="w-6 h-6 text-forest" strokeWidth={1.5} />
            <h2 className="font-serif text-2xl mt-3">Password updated.</h2>
            <p className="text-sm text-moss mt-2">Any lockout on your account was cleared. Taking you to sign in…</p>
            <Link to="/login" className="mt-4 inline-block text-sm text-forest font-medium" data-testid="reset-password-login-link">Sign in now →</Link>
          </div>
        ) : (
          <form onSubmit={submit} className="mt-10 space-y-5" data-testid="reset-password-form">
            <div>
              <label className="text-xs uppercase tracking-[0.2em] font-semibold text-sage">New password</label>
              <input type="password" required minLength={6} value={pw} onChange={(e) => setPw(e.target.value)} autoFocus
                className="mt-2 w-full bg-cream border border-sand rounded-lg px-4 py-3 focus:ring-2 focus:ring-forest focus:border-transparent outline-none"
                data-testid="reset-password-new" />
              <p className="mt-1.5 text-xs text-sage">Minimum 6 characters.</p>
            </div>
            <div>
              <label className="text-xs uppercase tracking-[0.2em] font-semibold text-sage">Confirm password</label>
              <input type="password" required minLength={6} value={pw2} onChange={(e) => setPw2(e.target.value)}
                className="mt-2 w-full bg-cream border border-sand rounded-lg px-4 py-3 focus:ring-2 focus:ring-forest focus:border-transparent outline-none"
                data-testid="reset-password-confirm" />
            </div>
            {error && <div className="text-sm text-clay bg-clay-light px-4 py-3 rounded-lg" data-testid="reset-password-error">{error}</div>}
            <button type="submit" disabled={loading}
              className="w-full bg-forest text-white rounded-lg px-6 py-3.5 font-medium hover:bg-forest-deep disabled:opacity-60 transition-colors inline-flex items-center justify-center gap-2"
              data-testid="reset-password-submit">
              <KeyRound className="w-4 h-4" /> {loading ? "Saving…" : "Set new password"}
            </button>
          </form>
        )}
      </div>
    </div>
  );
}
